import "react-toggle/style.css";

import { clientConfig, filterTemplate } from "@quickpoint.me/shared";
import { Suspense, lazy, useState } from "react";
import {
	useRecoilRefresher_UNSTABLE,
	useRecoilState,
	useRecoilValue,
	useSetRecoilState,
} from "recoil";
import { ThemeProvider } from "styled-components";
import { HexColorPicker } from "react-colorful";
import Toggle from "react-toggle";

import Done from "../../../icons/Done.svg?react";
import {
	NeueroDivergentReader,
	NeuroDivergentColorChoice,
	NeuroDivergentFontMultiplier,
	PresetColorChoices,
	ReadingRulerColor,
	ReadingRulerMode,
	ReadingRulerOpacity,
	ReadingTintMode,
	UseNDRColors,
} from "../../../state/accessibility";
import { FilterName, FilterSelector } from "../../../state/filter";
import { NavigationType } from "../../../state/navigation";
import { PageSizeAtom } from "../../../state/page";
import { StackCfKey } from "../../../state/stack";
import {
	ShowInfoBarAtom,
	ShowSettingsAtom,
	ShowSourceCodeAtom,
} from "../../../state/ui";

import { OVERLAY_PRESETS } from "./constants";
import {
	Accordion,
	AccordionContent,
	AccordionSummary,
	ColorPickerContainer,
	ControlRow,
	ControlRowInner,
	DoneButton,
	ExportPdfButton,
	FooterContent,
	HintText,
	SettingsContainer,
	SettingsContent,
	SettingsFooter,
	SettingsTitle,
	SettingsWrapper,
	SourceCodeButton,
	SourceCodeContainer,
	SourceCodeHeader,
	Spinner,
	StyledRangeInput,
	StyledSelect,
	SubSection,
	SubSectionTitle,
	WarningText,
} from "./styled";
import type { PdfExportStatus, SectionType, ThemeProps } from "./types";

const SourceCodeViewer = lazy(() => import("../SourceCodeViewer"));

export default function Settings() {
	const [showInfoBar, setShowInfoBar] = useRecoilState(ShowInfoBarAtom);
	const [showAssistiveTech, setShowAssistiveTech] = useRecoilState(
		NeueroDivergentReader,
	);
	const [useNDRColors, setUseNDRColors] = useRecoilState(UseNDRColors);
	const [readingRulerMode, setReadingRulerMode] =
		useRecoilState(ReadingRulerMode);
	const [readingTintMode, setReadingTintMode] = useRecoilState(ReadingTintMode);
	const [readingRulerColor, setReadingRulerColor] =
		useRecoilState(ReadingRulerColor);
	const [readingRulerOpacity, setReadingRulerOpacity] =
		useRecoilState(ReadingRulerOpacity);
	const setShowSettings = useSetRecoilState(ShowSettingsAtom);
	const [showSourceCode, setShowSourceCode] =
		useRecoilState(ShowSourceCodeAtom);
	const [neuroDivergentColor, setNeuroDivergentColor] = useRecoilState(
		NeuroDivergentColorChoice,
	);
	const [neuroDivergentFontMultiplier, setNeuroDivergentFontMultiplier] =
		useRecoilState(NeuroDivergentFontMultiplier);
	const presetColorChoices = useRecoilValue(PresetColorChoices);
	const currentFilter = useRecoilValue(FilterName);
	const navigationType = useRecoilValue(NavigationType);
	const resetFilter = useRecoilRefresher_UNSTABLE(
		FilterSelector(currentFilter),
	);
	const [pageSize, setPageSize] = useRecoilState(PageSizeAtom);
	const stackCfKey = useRecoilValue(StackCfKey);
	const [sliderHasMoved, setSliderHasMoved] = useState(false);
	const [pdfExportStatus, setPdfExportStatus] = useState<PdfExportStatus>("idle");
	const [openSection, setOpenSection] = useState<SectionType | null>(null);

	// Theme props for styled components
	const themeProps: ThemeProps = {
		$useNdrColors: useNDRColors,
		$background: neuroDivergentColor.background,
		$text: neuroDivergentColor.text,
	};

	const toggleSection = (section: SectionType) => {
		setOpenSection((current) => (current === section ? null : section));
	};

	const handleMultiplierChange = (newMultiplier: number) => {
		if (!sliderHasMoved) {
			setSliderHasMoved(true);
		}
		if (!neuroDivergentFontMultiplier || neuroDivergentFontMultiplier === 0)
			return;

		const originalBaseFontSize =
			pageSize.baseFontSize / neuroDivergentFontMultiplier;
		const newBaseFontSize = originalBaseFontSize * newMultiplier;

		setPageSize({
			...pageSize,
			baseFontSize: newBaseFontSize,
		});
		setNeuroDivergentFontMultiplier(newMultiplier);
	};

	const handleRulerPresetChange = (
		event: React.ChangeEvent<HTMLSelectElement>,
	) => {
		const val = event.target.value;
		if (val === "custom") return;
		const preset = OVERLAY_PRESETS.find((p) => p.name === val);
		if (preset) {
			setReadingRulerColor(preset.color);
			setReadingRulerOpacity(preset.opacity);
		}
	};

	const setNdrTextCC = (color: string) => {
		setNeuroDivergentColor({
			text: color,
			background: neuroDivergentColor.background,
		});
	};

	const setNdrBackgroundCC = (color: string) => {
		setNeuroDivergentColor({
			text: neuroDivergentColor.text,
			background: color,
		});
	};

	const handlePresetChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		if (event.target.value === "choose") return;
		const selectedPreset = presetColorChoices.find(
			(preset) => preset.name === event.target.value,
		);
		if (selectedPreset) {
			setNeuroDivergentColor({
				text: selectedPreset.value.text,
				background: selectedPreset.value.background,
			});
		}
	};

	const handleExportPdf = async () => {
		if (!stackCfKey || stackCfKey === "inAuthor") return;

		setPdfExportStatus("loading");
		try {
			const parts = stackCfKey.split("/");
			if (parts.length < 2) {
				throw new Error("Invalid stack key");
			}
			const owner = parts[0];
			const stack = parts.slice(1).join("/");

			const pdfUrl = clientConfig.pdfExport(owner, stack);

			const response = await fetch(pdfUrl);
			if (!response.ok) {
				throw new Error("Failed to generate PDF");
			}

			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${stack.replace(/\//g, "-")}.pdf`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			setPdfExportStatus("idle");
		} catch (error) {
			console.error("PDF export error:", error);
			setPdfExportStatus("error");
			setTimeout(() => setPdfExportStatus("idle"), 3000);
		}
	};

	if (showSourceCode) {
		return (
			<SourceCodeContainer>
				<SourceCodeHeader>
					<SourceCodeButton type="button" onClick={() => setShowSourceCode(false)}>
						Back to Settings
					</SourceCodeButton>
					<SourceCodeButton type="button" onClick={() => setShowSettings(false)}>
						Close
					</SourceCodeButton>
				</SourceCodeHeader>
				<Suspense fallback={<div>Loading...</div>}>
					<SourceCodeViewer />
				</Suspense>
			</SourceCodeContainer>
		);
	}

	return (
		<ThemeProvider theme={filterTemplate.button}>
			<SettingsContainer
				{...themeProps}
				$sliderHasMoved={sliderHasMoved}
				role="dialog"
				aria-modal="true"
				aria-labelledby="settings-title"
			>
				<SettingsWrapper>
					<SettingsContent>
						<SettingsTitle id="settings-title">Accessibility Settings</SettingsTitle>

						{stackCfKey && stackCfKey !== "inAuthor" && (
							<ExportPdfButton
								{...themeProps}
								type="button"
								onClick={handleExportPdf}
								disabled={pdfExportStatus === "loading"}
							>
								{pdfExportStatus === "loading" && (
									<Spinner {...themeProps} aria-hidden="true" />
								)}
								{pdfExportStatus === "loading"
									? "Generating PDF..."
									: pdfExportStatus === "error"
										? "Export Failed - Try Again"
										: "Export as PDF"}
							</ExportPdfButton>
						)}

						{/* Colors & Contrast Accordion */}
						<Accordion {...themeProps} open={openSection === "colors"}>
							<AccordionSummary
								{...themeProps}
								onClick={(e) => {
									e.preventDefault();
									toggleSection("colors");
								}}
							>
								Colors & Contrast
							</AccordionSummary>
							<AccordionContent>
								<ControlRow>
									<label htmlFor="useNDRColors-toggle">
										Use High Contrast / Custom Colors
									</label>
									{/* @ts-ignore */}
									<Toggle
										id="useNDRColors-toggle"
										className="qp-react-toggle"
										defaultChecked={useNDRColors}
										onChange={() => {
											setUseNDRColors(!useNDRColors);
											resetFilter();
										}}
									/>
								</ControlRow>

								{useNDRColors && (
									<SubSection {...themeProps}>
										<SubSectionTitle>Customize Colors</SubSectionTitle>
										<ControlRow $column>
											<label htmlFor="preset-color-scheme-select">
												Preset Themes
											</label>
											<StyledSelect
												{...themeProps}
												id="preset-color-scheme-select"
												onChange={handlePresetChange}
												defaultValue="choose"
											>
												<option value="choose" disabled>
													Select a Preset...
												</option>
												{presetColorChoices.map((preset) => (
													<option key={preset.name} value={preset.name}>
														{preset.name}
													</option>
												))}
											</StyledSelect>
										</ControlRow>

										<ControlRow $column>
											<label>Text Color</label>
											<ColorPickerContainer>
												<HexColorPicker
													color={neuroDivergentColor.text}
													onChange={setNdrTextCC}
												/>
											</ColorPickerContainer>
										</ControlRow>

										<ControlRow $column>
											<label>Background Color</label>
											<ColorPickerContainer>
												<HexColorPicker
													color={neuroDivergentColor.background}
													onChange={setNdrBackgroundCC}
												/>
											</ColorPickerContainer>
										</ControlRow>
									</SubSection>
								)}
							</AccordionContent>
						</Accordion>

						{/* Readability Accordion */}
						<Accordion {...themeProps} open={openSection === "readability"}>
							<AccordionSummary
								{...themeProps}
								onClick={(e) => {
									e.preventDefault();
									toggleSection("readability");
								}}
							>
								Readability
							</AccordionSummary>
							<AccordionContent>
								<ControlRow>
									<label htmlFor="showAssistiveTech-toggle">
										Use Neuro Divergent Font
									</label>
									{/* @ts-ignore */}
									<Toggle
										id="showAssistiveTech-toggle"
										className="qp-react-toggle"
										defaultChecked={showAssistiveTech}
										onChange={() => setShowAssistiveTech(!showAssistiveTech)}
									/>
								</ControlRow>

								<ControlRow $column>
									<ControlRowInner>
										<label htmlFor="readingRulerMode-toggle">
											Reading Ruler / Scrim
										</label>
										{/* @ts-ignore */}
										<Toggle
											id="readingRulerMode-toggle"
											className="qp-react-toggle"
											checked={readingRulerMode}
											disabled={navigationType !== "tap"}
											onChange={() => setReadingRulerMode(!readingRulerMode)}
										/>
									</ControlRowInner>
									{navigationType !== "tap" && (
										<WarningText>
											Only available in "Tap" navigation mode.
										</WarningText>
									)}
								</ControlRow>

								<ControlRow>
									<label htmlFor="readingTintMode-toggle">
										Reading Tint / Overlay
									</label>
									{/* @ts-ignore */}
									<Toggle
										id="readingTintMode-toggle"
										className="qp-react-toggle"
										checked={readingTintMode}
										onChange={() => {
											if (!readingTintMode && !readingRulerMode) {
												setReadingRulerOpacity(0.5);
											}
											setReadingTintMode(!readingTintMode);
										}}
									/>
								</ControlRow>

								{(readingRulerMode || readingTintMode) && (
									<SubSection {...themeProps}>
										<SubSectionTitle>Overlay Settings</SubSectionTitle>
										<ControlRow $column>
											<label htmlFor="ruler-preset-select">Presets</label>
											<StyledSelect
												{...themeProps}
												id="ruler-preset-select"
												onChange={handleRulerPresetChange}
												defaultValue="custom"
											>
												<option value="custom" disabled>
													Select a Preset...
												</option>
												{OVERLAY_PRESETS.map((preset) => (
													<option key={preset.name} value={preset.name}>
														{preset.name}
													</option>
												))}
											</StyledSelect>
										</ControlRow>
										<ControlRow $column>
											<label>Overlay Color</label>
											<ColorPickerContainer>
												<HexColorPicker
													color={readingRulerColor || "#000000"}
													onChange={setReadingRulerColor}
												/>
											</ColorPickerContainer>
										</ControlRow>
										<ControlRow $column>
											<label>
												Opacity:{" "}
												{Math.round((readingRulerOpacity || 0.85) * 100)}%
											</label>
											<StyledRangeInput
												{...themeProps}
												type="range"
												min="0.1"
												max="1"
												step="0.05"
												value={readingRulerOpacity || 0.85}
												onChange={(e) =>
													setReadingRulerOpacity(
														Number.parseFloat(e.target.value),
													)
												}
											/>
										</ControlRow>
									</SubSection>
								)}
							</AccordionContent>
						</Accordion>

						{/* Text Size Accordion */}
						<Accordion {...themeProps} open={openSection === "textSize"}>
							<AccordionSummary
								{...themeProps}
								onClick={(e) => {
									e.preventDefault();
									toggleSection("textSize");
								}}
							>
								Text Size
							</AccordionSummary>
							<AccordionContent>
								<ControlRow $column>
									<label htmlFor="font-size-slider">
										Font Multiplier:{" "}
										{Math.round(neuroDivergentFontMultiplier * 100)}%
									</label>
									<StyledRangeInput
										{...themeProps}
										id="font-size-slider"
										type="range"
										min="1"
										max="4"
										step="0.1"
										value={neuroDivergentFontMultiplier}
										onChange={(e) =>
											handleMultiplierChange(Number.parseFloat(e.target.value))
										}
									/>
								</ControlRow>
							</AccordionContent>
						</Accordion>

						{/* Display Options Accordion */}
						<Accordion {...themeProps} open={openSection === "display"}>
							<AccordionSummary
								{...themeProps}
								onClick={(e) => {
									e.preventDefault();
									toggleSection("display");
								}}
							>
								Display Options
							</AccordionSummary>
							<AccordionContent>
								<ControlRow>
									<label htmlFor="showInfoBar-toggle">Show Page Numbers</label>
									{/* @ts-ignore */}
									<Toggle
										id="showInfoBar-toggle"
										className="qp-react-toggle"
										defaultChecked={showInfoBar}
										onChange={() => setShowInfoBar(!showInfoBar)}
									/>
								</ControlRow>
								<ControlRow>
									<label htmlFor="showSourceCode-toggle">Show Source Code</label>
									{/* @ts-ignore */}
									<Toggle
										id="showSourceCode-toggle"
										className="qp-react-toggle"
										defaultChecked={showSourceCode}
										onChange={() => setShowSourceCode(!showSourceCode)}
									/>
								</ControlRow>
								<HintText>
									Enabling Source Code will switch to the code viewer view.
								</HintText>
							</AccordionContent>
						</Accordion>
					</SettingsContent>
				</SettingsWrapper>

				<SettingsFooter {...themeProps}>
					<FooterContent>
						<DoneButton
							{...themeProps}
							type="button"
							onClick={() => setShowSettings(false)}
						>
							<Done width={20} height={20} fill="currentColor" /> Done
						</DoneButton>
					</FooterContent>
				</SettingsFooter>
			</SettingsContainer>
		</ThemeProvider>
	);
}
